import { useCallback, useRef, useState } from 'react';
import toaster from '../../utils/toaster';
import { POPUP_DELAY, TOASTER_TRANSLATIONS } from '../consts';

export const useImprovePrompt = ({
    formContent,
    hasRequiredFields,
    dimensionDisplayName,
    translate,
    improveFetcher,
    onContentChange,
}) => {
    const [isImproving, setIsImproving] = useState(false);
    const improveSnapshotRef = useRef({ original: null, improved: null });

    const canRevert = !isImproving && !!improveSnapshotRef.current.original;

    const isAlreadyImproved = () => {
        if (!improveSnapshotRef.current.improved) return false;

        if (Array.isArray(formContent))
            return JSON.stringify(formContent) === JSON.stringify(improveSnapshotRef.current.improved);

        return formContent === improveSnapshotRef.current.improved;
    };

    const shouldDisableImprove = isImproving || !hasRequiredFields || isAlreadyImproved();

    const clearImproveSnapshot = useCallback(() => {
        improveSnapshotRef.current = { original: null, improved: null };
    }, []);

    const onImprove = useCallback(async () => {
        if (!hasRequiredFields) {
            toaster.error(translate(TOASTER_TRANSLATIONS.IMPROVE_MISSING_FIELDS), {
                autoClose: POPUP_DELAY,
            });
            return;
        }

        setIsImproving(true);

        try {
            const currentContent = formContent;
            const improvedContent = await improveFetcher(currentContent, dimensionDisplayName);
            improveSnapshotRef.current = { original: currentContent, improved: improvedContent };
            onContentChange(improvedContent);
        } catch (error) {
            clearImproveSnapshot();
            toaster.error(translate(TOASTER_TRANSLATIONS.IMPROVE_FIELD_ERROR), { autoClose: POPUP_DELAY });
        } finally {
            setIsImproving(false);
        }
    }, [dimensionDisplayName, formContent, hasRequiredFields, improveFetcher, onContentChange]);

    const onRevert = useCallback(() => {
        if (!canRevert) return;

        onContentChange(improveSnapshotRef.current.original);
        clearImproveSnapshot();
    }, [canRevert, onContentChange]);

    const hasContentChangedAfterImprove = useCallback(newContent => {
        if (!improveSnapshotRef.current.original) return false;

        if (Array.isArray(newContent))
            return JSON.stringify(newContent) !== JSON.stringify(improveSnapshotRef.current.original);

        return newContent?.trim() !== improveSnapshotRef.current.original?.trim();
    }, []);

    const handleContentChange = useCallback(
        newContent => {
            onContentChange(newContent);
            if (hasContentChangedAfterImprove(newContent)) {
                clearImproveSnapshot();
            }
        },
        [onContentChange, hasContentChangedAfterImprove, clearImproveSnapshot]
    );

    return {
        isImproving,
        canRevert,
        shouldDisableImprove,
        onImprove,
        onRevert,
        clearImproveSnapshot,
        handleContentChange,
    };
};
